import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  jsonLd?: object[];
  ogImage?: string;
  twitterImage?: string;
  hreflangLinks?: { lang: string; url: string }[];
  noindex?: boolean;
  type?: "website" | "article";
}

export const SEOHead = ({ title, description, keywords, canonicalUrl, jsonLd, ogImage, twitterImage, hreflangLinks, noindex, type }: SEOHeadProps) => {
  const location = useLocation();
  const finalCanonicalUrl = canonicalUrl;
  
  useEffect(() => {
    // Update title
    document.title = title;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    // Set robots meta tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    if (noindex) {
      metaRobots.setAttribute('content', 'noindex, follow');
    } else {
      metaRobots.setAttribute('content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }
    
    // Note: <meta name="keywords"> has been ignored by Google since 2009 and is
    // a recognized spam signal when stuffed. We accept the `keywords` prop for
    // backward compatibility but intentionally do NOT render it. Also remove any
    // pre-existing keywords meta that may have been left in index.html.
    const existingKeywords = document.querySelector('meta[name="keywords"]');
    if (existingKeywords) {
      existingKeywords.remove();
    }
    void keywords;
    
    // Update canonical URL if provided
    if (finalCanonicalUrl) {
      // Ensure URL is absolute
      let normalizedCanonical = finalCanonicalUrl;
      if (!normalizedCanonical.startsWith('http')) {
        normalizedCanonical = `https://noq5.ai${normalizedCanonical.startsWith('/') ? normalizedCanonical : '/' + normalizedCanonical}`;
      }
      
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', normalizedCanonical);
    }
    
    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', title);
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', description);
    
    // Set og:url to canonical URL if provided
    if (finalCanonicalUrl) {
      let ogUrl = document.querySelector('meta[property="og:url"]');
      if (!ogUrl) {
        ogUrl = document.createElement('meta');
        ogUrl.setAttribute('property', 'og:url');
        document.head.appendChild(ogUrl);
      }
      // Ensure URL is absolute
      let normalizedCanonical = finalCanonicalUrl;
      if (!normalizedCanonical.startsWith('http')) {
        normalizedCanonical = `https://noq5.ai${normalizedCanonical.startsWith('/') ? normalizedCanonical : '/' + normalizedCanonical}`;
      }
      ogUrl.setAttribute('content', normalizedCanonical);
    }
    
    // Set og:type
    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) {
      ogType = document.createElement('meta');
      ogType.setAttribute('property', 'og:type');
      document.head.appendChild(ogType);
    }
    ogType.setAttribute('content', type || 'website');
    
    // Update Twitter Card tags
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (!twitterTitle) {
      twitterTitle = document.createElement('meta');
      twitterTitle.setAttribute('name', 'twitter:title');
      document.head.appendChild(twitterTitle);
    }
    twitterTitle.setAttribute('content', title);
    
    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (!twitterDescription) {
      twitterDescription = document.createElement('meta');
      twitterDescription.setAttribute('name', 'twitter:description');
      document.head.appendChild(twitterDescription);
    }
    twitterDescription.setAttribute('content', description);
    
    // Update images if provided
    if (ogImage) {
      const ogImg = document.querySelector('meta[property="og:image"]');
      if (ogImg) {
        ogImg.setAttribute('content', ogImage);
      }
    }
    
    if (twitterImage) {
      const twitterImg = document.querySelector('meta[name="twitter:image"]');
      if (twitterImg) {
        twitterImg.setAttribute('content', twitterImage);
      }
    }
    
    // Inject JSON-LD schemas
    if (jsonLd && jsonLd.length > 0) {
      // Remove existing injected schemas
      const existingSchemas = document.querySelectorAll('script[type="application/ld+json"][data-seo-injected]');
      existingSchemas.forEach(schema => schema.remove());
      
      // Add new schemas
      jsonLd.forEach((schema, index) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-injected', 'true');
        script.setAttribute('data-schema-index', index.toString());
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }
    
    // Add hreflang tags - always include self-referencing hreflang
    // Remove existing hreflang tags
    const existingHreflangs = document.querySelectorAll('link[rel="alternate"][hreflang][data-seo-injected]');
    existingHreflangs.forEach(link => link.remove());
    
    if (hreflangLinks && hreflangLinks.length > 0) {
      // Add provided hreflang tags
      hreflangLinks.forEach(({ lang, url }) => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        link.setAttribute('href', url);
        link.setAttribute('data-seo-injected', 'true');
        document.head.appendChild(link);
      });
    } else if (finalCanonicalUrl) {
      // If no hreflang provided, add self-referencing hreflang
      const normalizedUrl = finalCanonicalUrl.startsWith('http') 
        ? finalCanonicalUrl 
        : `https://noq5.ai${finalCanonicalUrl.startsWith('/') ? finalCanonicalUrl : '/' + finalCanonicalUrl}`;
      
      const lang = 'en';
      const selfLink = document.createElement('link');
      selfLink.setAttribute('rel', 'alternate');
      selfLink.setAttribute('hreflang', lang);
      selfLink.setAttribute('href', normalizedUrl);
      selfLink.setAttribute('data-seo-injected', 'true');
      document.head.appendChild(selfLink);
      
      // Add x-default pointing to self for single-language pages
      const defaultLink = document.createElement('link');
      defaultLink.setAttribute('rel', 'alternate');
      defaultLink.setAttribute('hreflang', 'x-default');
      defaultLink.setAttribute('href', normalizedUrl);
      defaultLink.setAttribute('data-seo-injected', 'true');
      document.head.appendChild(defaultLink);
    }
  }, [title, description, keywords, finalCanonicalUrl, jsonLd, ogImage, twitterImage, hreflangLinks, noindex, type, location.pathname]);

  return null;
};